import { NavLink, Link, useLocation } from "react-router-dom";
import {
    LayoutDashboard,
    Users,
    CalendarClock,
    LineChart,
    Plus,
} from "lucide-react";
import { Button } from "./ui/button";
import Brand from "./Brand";
import WorkspaceSwitcher from "./WorkspaceSwitcher";
import LastSavedBadge from "./LastSavedBadge";
import { useFeedback } from "../context/FeedbackContext";
import { useAuth } from "../context/AuthContext";

const NAV = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/students", label: "Students", icon: Users, end: false },
    { to: "/makeups", label: "Make-ups", icon: CalendarClock, end: false },
    { to: "/reports", label: "Reports", icon: LineChart, end: false },
];

export default function Layout({ children }) {
    const location = useLocation();
    const { feedbackMode, toggleFeedbackMode } = useFeedback();
    const { isAdmin } = useAuth();

    return (
        <div className="min-h-screen bg-[#F7F8FA] text-ink">
            {/* Top Bar */}
            <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-mist">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-8 min-w-0">
                        <Brand variant="wordmark" size="md" testId="brand-link" />

                        <nav
                            className="hidden md:flex items-center gap-1 bg-mist/60 border border-mist rounded-2xl p-1"
                            data-testid="main-nav"
                        >
                            {NAV.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.end}
                                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={({ isActive }) =>
                                        `flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                                            isActive
                                                ? "bg-white text-action shadow-sm"
                                                : "text-ink-mute hover:text-ink"
                                        }`
                                    }
                                >
                                    <item.icon size={16} strokeWidth={2.2} />
                                    {item.label}
                                </NavLink>
                            ))}
                        </nav>
                    </div>

                    <div className="flex items-center gap-2">
                        <Link to="/students/new" data-testid="header-add-student-btn">
                            <Button className="rounded-xl bg-action hover:bg-action-hover text-white font-medium px-4 sm:px-5 h-11 shadow-soft hidden sm:inline-flex">
                                <Plus size={18} strokeWidth={2.4} className="mr-1" />
                                <span>Add Student</span>
                            </Button>
                        </Link>
                        <LastSavedBadge />
                        <WorkspaceSwitcher />
                    </div>
                </div>

                {/* Mobile tabs */}
                <nav
                    className="md:hidden border-t border-mist px-2 py-2 flex items-center gap-1 overflow-x-auto"
                    data-testid="mobile-nav"
                >
                    {NAV.map((item) => {
                        const active =
                            (item.end && location.pathname === item.to) ||
                            (!item.end && location.pathname.startsWith(item.to) && item.to !== "/") ||
                            (item.to === "/" && location.pathname === "/");
                        return (
                            <Link
                                key={item.to}
                                to={item.to}
                                data-testid={`mnav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`flex-1 min-w-[88px] text-center px-3 py-2 rounded-lg text-xs font-semibold ${
                                    active
                                        ? "bg-action-soft text-action"
                                        : "text-ink-mute"
                                }`}
                            >
                                {item.label}
                            </Link>
                        );
                    })}
                </nav>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
                {children}
            </main>

            <footer className="max-w-7xl mx-auto px-4 sm:px-8 py-10 text-xs text-ink-mute flex items-center justify-between gap-3 flex-wrap">
                <span>Tempo · local-first, stored on your device</span>
                {isAdmin && (
                    <button
                        type="button"
                        onClick={toggleFeedbackMode}
                        className="focus-ring text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-mute hover:text-ink transition-colors"
                        data-testid="footer-feedback-toggle"
                        title="⌘+Shift+F"
                    >
                        {feedbackMode ? "Feedback Mode · ON" : "Feedback Mode"}
                    </button>
                )}
            </footer>
        </div>
    );
}
